/*
 * rtsmto-picker.js — TICK-4082
 *
 * Behaviour for <rtsmto-picker> (snippets/rtsmto-picker.liquid): the Ready to
 * Ship / Made to Order picker on the Wooden Stories templates.
 *
 *   READY TO SHIP  radio cards, one per in-stock variant, each with a split
 *                  swatch and a close-up (Iteration 4): desktop opens a card
 *                  above the swatch after a short hover (click pins it);
 *                  a phone shows the close-up inside the selected card (CSS).
 *   MADE TO ORDER  one radiogroup per option: swatch tiles for materials,
 *                  text or drawing buttons for the rest (Iteration 4). A
 *                  hidden native <select> per group stays the value store.
 *                  Tiles show a shared hover card after a short delay.
 *
 * Each tab keeps its own selection: switching back restores it. The active
 * tab is mirrored to [data-active-tab] and announced with 'rtsmto:tab' so
 * variant-availability.js can make the availability box (and the cart's
 * Availability property) follow the tab rather than the stock state —
 * Laleh's "tab says MADE TO ORDER, box says in stock, which one is it?".
 *
 * Selecting anything writes the per-option hidden inputs and dispatches ONE
 * bubbling 'change' on the last input written — exactly like selectRow() in
 * combined-variant-select.js. The theme's ProductFormReader derives the
 * variant from those inputs and fires theme:variant:change, which drives
 * price/image/ATC/URL history and variant-availability.js (availability box,
 * quantity split copy, Availability line-item property). We never touch
 * input[name="id"].
 *
 * Variant data: [data-variant-stock-map] (id → {s, l, o, q}) plus the
 * theme:product:variants-hydrated event theme.js fires for 250+ variant
 * products (hydrateVariantPages appends the late variants to the product
 * JSON BEFORE firing it, so a late row resolves once it exists).
 *
 * Swatch chain (see the snippet header): server map (native swatch, then
 * Content → Files "swatch-<handle>.jpg") → exact overrides → generated
 * Swatch King CDN URL, probed with Image() → neutral chip (CSS default).
 * A 'combo' option ("Heritage | Saddle and Walnut") paints as two parts:
 * the text before the last " and " as upholstery, the rest as a finish.
 */
(function () {
  'use strict';

  if (customElements.get('rtsmto-picker')) return;

  var MATERIAL = { upholstery: 'Upholstery', top: 'Finish', base: 'Finish', swatch: 'Upholstery' };
  var COMBO_SPLIT = ' and ';
  var SK_BASE = 'https://cdn.starapps.studio/v2/apps/vsk/sobu/swatches/';
  var VISIBLE_ROWS = 5;
  var COLLAPSE_OVER = 6;
  var HYDRATE_WAIT_MS = 4000;
  var probeCache = {}; // url → true | false | [callbacks]
  // Hover cards only where there is a real hover and room for them; phones
  // and touch screens get the in-card expand instead.
  var HOVER_MQ = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 768px)') : null;
  function canHover() { return !!(HOVER_MQ && HOVER_MQ.matches); }

  function readJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function normalizeKey(k) {
    return String(k).toLowerCase().replace(/\s/g, '');
  }

  function sameOptions(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) {
      if (String(a[i]) !== String(b[i])) return false;
    }
    return true;
  }

  // Swatch King's CDN file naming: space → %2520, | → ~7C, anything else
  // URL-encoded with its % escaped again (matches the override URLs).
  function skEncode(value) {
    return String(value).split('').map(function (ch) {
      if (ch === ' ') return '%2520';
      if (ch === '|') return '~7C';
      return encodeURIComponent(ch).replace(/%/g, '%25');
    }).join('');
  }

  // "Heritage | Saddle and Walnut" → ["Heritage | Saddle", "Walnut"].
  function splitCombo(value) {
    var v = String(value);
    var at = v.lastIndexOf(COMBO_SPLIT);
    if (at === -1) return [v, ''];
    return [v.slice(0, at), v.slice(at + COMBO_SPLIT.length)];
  }

  function swatchCss(swatch) {
    if (swatch && swatch.img) return 'url("' + String(swatch.img).replace(/"/g, '%22') + '")';
    return '';
  }

  var ddCount = 0;

  function probe(url, cb) {
    var cached = probeCache[url];
    if (cached === true || cached === false) { cb(cached); return; }
    if (cached) { cached.push(cb); return; }
    probeCache[url] = [cb];
    var img = new Image();
    function done(ok) {
      var waiting = probeCache[url];
      probeCache[url] = ok;
      if (Array.isArray(waiting)) waiting.forEach(function (fn) { fn(ok); });
    }
    img.onload = function () { done(img.naturalWidth > 0); };
    img.onerror = function () { done(false); };
    img.src = url;
  }

  class RtsmtoPicker extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-section-type="product"]') || document;

      this.map = readJSON(this.section.querySelector('[data-variant-stock-map]')) || {};
      this.order = Object.keys(this.map);
      var stockI18n = readJSON(this.section.querySelector('[data-stock-i18n]')) || {};
      this.strings = readJSON(this.querySelector('[data-rtsmto-i18n]')) || {};
      this.optionNames = Array.isArray(stockI18n.optionNames) ? stockI18n.optionNames.slice() : [];
      this.roles = Array.isArray(this.strings.roles) ? this.strings.roles : [];
      this.chipParts = Array.isArray(this.strings.chipParts) ? this.strings.chipParts : [];

      this.swatches = {};
      var raw = readJSON(this.querySelector('[data-rtsmto-swatches]')) || {};
      Object.keys(raw).forEach(function (key) {
        var parts = key.split('||');
        this.swatches[normalizeKey(parts[0]) + '||' + normalizeKey(parts[1] || '')] = raw[key];
      }, this);
      this.overrides = {};
      var ov = readJSON(this.querySelector('[data-rtsmto-swatch-overrides]')) || {};
      Object.keys(ov).forEach(function (key) {
        var parts = key.split('||');
        this.overrides[normalizeKey(parts[0]) + '||' + normalizeKey(parts[1] || '')] = ov[key];
      }, this);

      this.variantsCount = parseInt(this.getAttribute('data-variants-count'), 10) || 0;
      // Late (251+) variants: variantsCount comes from product.variants_count;
      // if that is missing, a full 250-entry map means there may be more.
      this.hasLatePages = this.variantsCount
        ? this.variantsCount > 250
        : this.order.length >= 250;
      this.hydrated = !this.hasLatePages;
      this.pendingUnresolved = false;
      this.handle = (this.section.getAttribute && this.section.getAttribute('data-product-handle')) || '';
      this.lastRtsId = null;

      this.toggle = this.querySelector('[data-rtsmto-toggle]');
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-rtsmto-tab]'));
      this.rowsEl = this.querySelector('[data-rtsmto-rows]');
      this.moreBtn = this.querySelector('[data-rtsmto-more]');
      this.note = this.querySelector('[data-rtsmto-note]');
      this.selects = Array.prototype.slice.call(this.querySelectorAll('[data-rtsmto-select]'));
      this.groups = Array.prototype.slice.call(this.querySelectorAll('[data-rtsmto-group]'));
      this.pop = this.querySelector('[data-rtsmto-pop]');
      this.zoomDelay = parseInt(this.getAttribute('data-zoom-delay'), 10);
      if (isNaN(this.zoomDelay)) this.zoomDelay = 100;
      this.tileDelay = parseInt(this.getAttribute('data-tile-delay'), 10);
      if (isNaN(this.tileDelay)) this.tileDelay = 250;
      this.labels = Array.isArray(this.strings.labels) ? this.strings.labels : [];
      this.zoomOpen = null;   // the row whose close-up is showing (desktop)
      this.zoomPinned = false;
      this.mtoOptions = null; // Made to Order's own selection, restored on switch back
      this.collections = {};
      var colls = this.strings.collections || {};
      Object.keys(colls).forEach(function (k) { this.collections[k.toLowerCase()] = colls[k]; }, this);
      if (!this.drawers) this.drawers = Array.prototype.slice.call(this.querySelectorAll('[data-rtsmto-drawer]'));
      this.expanded = this.moreBtn ? this.moreBtn.getAttribute('aria-expanded') === 'true' : true;
      // Late rows can push a short list past the collapse threshold; only
      // re-decide the collapse then if the shopper hasn't touched the button.
      this.userToggledMore = false;
      this.wasCollapsible = this.rows().length > COLLAPSE_OVER;

      var checked = this.rowsEl && this.rowsEl.querySelector('.rtsmto-row__input:checked');
      if (checked) this.lastRtsId = checked.value;

      if (!this.handlersBound) {
        this.handlersBound = true;
        this.onChange = this.onChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onVariantsHydrated = this.onVariantsHydrated.bind(this);
        this.onOver = this.onOver.bind(this);
        this.onOut = this.onOut.bind(this);
        this.onFocusIn = this.onFocusIn.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
      }
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('pointerover', this.onOver);
      this.addEventListener('pointerout', this.onOut);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('theme:variant:change', this.onVariantChange);
      document.addEventListener('theme:product:variants-hydrated', this.onVariantsHydrated);
      if (!this.onDocPointer) {
        this.onDocPointer = this.onDocPointer_.bind(this);
      }
      document.addEventListener('pointerdown', this.onDocPointer);

      this.paintAll();
      this.filterChoices();
      if (this.activeTab() === 'mto') this.mtoOptions = this.currentOptions();
      this.updateMore();
      this.updateNote(this.currentOptions());

      // theme.js may have fired theme:product:variants-hydrated before this
      // element connected (it is not replayed). If no event arrives within
      // HYDRATE_WAIT_MS, fetch the late pages ourselves through the same
      // Section Rendering route theme.js uses. mergeHydrated() skips ids it
      // already has, so the event path and this fallback can both run.
      if (this.hasLatePages && !this.hydrated && this.handle) {
        var self = this;
        clearTimeout(this.hydrateTimer);
        this.hydrateTimer = setTimeout(function () {
          if (!self.hydrated) self.fetchLatePages();
        }, HYDRATE_WAIT_MS);
      }
    }

    fetchLatePages() {
      if (this.fetchingLate) return;
      this.fetchingLate = true;
      var self = this;
      var root = (window.theme && window.theme.routes && window.theme.routes.root_url) || '/';
      if (root.charAt(root.length - 1) !== '/') root += '/';
      var extra = {};
      function page(n) {
        if (n > 9) return Promise.resolve();
        var url = root + 'products/' + encodeURIComponent(self.handle) + '?section_id=api-product-variants&page=' + n;
        return window.fetch(url).then(function (response) {
          if (!response.ok) return null;
          return response.text();
        }).then(function (text) {
          if (!text) return;
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var data = readJSON(doc.querySelector('[data-api-variants]'));
          if (!data) return;
          Object.keys(data.map || {}).forEach(function (id) { extra[id] = data.map[id]; });
          if (data.next === true) return page(n + 1);
        });
      }
      page(2).then(function () {
        if (Object.keys(extra).length) self.mergeHydrated(extra);
      }).catch(function () { /* the picker just keeps the first 250 */ });
    }

    disconnectedCallback() {
      clearTimeout(this.hydrateTimer);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeyDown);
      this.removeEventListener('pointerover', this.onOver);
      this.removeEventListener('pointerout', this.onOut);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      clearTimeout(this.zoomTimer);
      clearTimeout(this.tileTimer);
      if (this.drawerOpen) this.closeDrawer();
      document.removeEventListener('theme:variant:change', this.onVariantChange);
      document.removeEventListener('theme:product:variants-hydrated', this.onVariantsHydrated);
      document.removeEventListener('pointerdown', this.onDocPointer);
    }

    // ── Form bridge ────────────────────────────────────────────────────────

    inputFor(index) {
      return this.querySelector('[data-single-option-selector][data-index="option' + (index + 1) + '"]');
    }

    currentOptions() {
      var values = [];
      for (var i = 0; i < this.optionNames.length; i++) {
        var input = this.inputFor(i);
        if (!input) break;
        values.push(input.value);
      }
      return values;
    }

    // Mirrors combined-variant-select.js selectRow(): write every hidden
    // input, then ONE bubbling change on the last one written.
    writeOptions(values) {
      var lastInput = null;
      values.forEach(function (value, index) {
        var input = this.inputFor(index);
        if (!input) return;
        input.value = value;
        lastInput = input;
      }, this);
      if (lastInput) lastInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    findVariant(values) {
      for (var i = 0; i < this.order.length; i++) {
        var entry = this.map[this.order[i]];
        if (entry && sameOptions(entry.o, values)) return { id: this.order[i], entry: entry };
      }
      return null;
    }

    // No exact variant: best variant that keeps the changed value and matches
    // the most other current values; ties go to the variant matching the
    // earlier option positions, then to variant order.
    closestVariant(values, changedIndex) {
      var best = null;
      var bestScore = -1;
      var bestVector = '';
      this.order.forEach(function (id) {
        var entry = this.map[id];
        var o = entry && entry.o;
        if (!o || String(o[changedIndex]) !== String(values[changedIndex])) return;
        var score = 0;
        var vector = '';
        for (var i = 0; i < values.length; i++) {
          var hit = String(o[i]) === String(values[i]);
          if (hit && i !== changedIndex) score++;
          vector += hit ? '1' : '0';
        }
        if (score > bestScore || (score === bestScore && vector > bestVector)) {
          best = { id: id, entry: entry };
          bestScore = score;
          bestVector = vector;
        }
      }, this);
      return best;
    }

    // The made-to-order variant sharing the most values with `values`; ties go
    // to the earlier option positions (size first), then to variant order.
    nearestMto(values) {
      var best = null, bestScore = -1, bestVector = '';
      this.order.forEach(function (id) {
        var entry = this.map[id];
        if (!entry || !entry.o || entry.s === 'in_stock') return;
        var score = 0, vector = '';
        for (var i = 0; i < values.length; i++) {
          var hit = String(entry.o[i]) === String(values[i]);
          if (hit) score++;
          vector += hit ? '1' : '0';
        }
        if (score > bestScore || (score === bestScore && vector > bestVector)) {
          best = { id: id, entry: entry }; bestScore = score; bestVector = vector;
        }
      }, this);
      return best;
    }

    // ── Events ─────────────────────────────────────────────────────────────

    onChange(event) {
      var target = event.target;
      if (target.matches('.rtsmto-row__input')) {
        event.stopPropagation();
        var row = target.closest('[data-rtsmto-row]');
        if (!row) return;
        this.lastRtsId = target.value;
        this.markRow(target.value);
        var values = this.rowOptions(row);
        if (values.length) this.writeOptions(values);
        return;
      }
      if (target.matches('[data-rtsmto-select]')) {
        event.stopPropagation();
        this.onSelectChange(target);
      }
    }

    onSelectChange(select) {
      var index = parseInt(select.getAttribute('data-index'), 10) || 0;
      var values = this.currentOptions();
      values[index] = select.value;
      this.syncGroup(select);

      var exact = this.findVariant(values);
      if (exact) {
        this.pendingUnresolved = false;
        this.writeOptions(values);
        return;
      }
      // 250+ variants and the late pages aren't in yet: the combination may
      // well exist past the cap. Write it as chosen; onVariantsHydrated
      // re-fires the change once the theme's product JSON has it.
      if (!this.hydrated) {
        this.pendingUnresolved = true;
        this.writeOptions(values);
        return;
      }
      var closest = this.closestVariant(values, index);
      if (!closest) {
        this.writeOptions(values);
        return;
      }
      this.syncSelects(closest.entry.o);
      this.writeOptions(closest.entry.o);
    }

    // A tile / button in a Made to Order group: same path as a native pick.
    chooseValue(button) {
      var group = button.closest('[data-rtsmto-group]');
      var select = group && group.querySelector('[data-rtsmto-select]');
      if (!select) return;
      var value = button.getAttribute('data-value');
      this.hidePop();
      if (select.value === value) return;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onClick(event) {
      var opener = event.target.closest('[data-rtsmto-drawer-open]');
      if (opener && this.contains(opener)) {
        this.openDrawer(opener.getAttribute('data-rtsmto-drawer-open'), opener);
        return;
      }
      var choice = event.target.closest('[data-rtsmto-choice]');
      if (choice && this.contains(choice)) {
        this.chooseValue(choice);
        return;
      }
      var zoom = event.target.closest('[data-rtsmto-zoom-trigger]');
      if (zoom && this.contains(zoom)) {
        // A button inside the card's <label> never selects the card, so do
        // it by hand where there is no hover close-up (phones, touch).
        event.preventDefault();
        var zrow = zoom.closest('[data-rtsmto-row]');
        if (!canHover()) {
          var zin = zrow && zrow.querySelector('.rtsmto-row__input');
          if (zin && !zin.checked) { zin.checked = true; zin.dispatchEvent(new Event('change', { bubbles: true })); }
          return;
        }
        clearTimeout(this.zoomTimer);
        if (this.zoomOpen === zrow && this.zoomPinned) { this.closeZoom(); return; }
        this.openZoom(zrow, true);
        return;
      }
      var tab = event.target.closest('[data-rtsmto-tab]');
      if (tab && this.contains(tab)) {
        this.activateTab(tab.getAttribute('data-rtsmto-tab'), false);
        return;
      }
      if (event.target.closest('[data-rtsmto-more]')) {
        this.expanded = !this.expanded;
        this.userToggledMore = true;
        this.updateMore();
        return;
      }
      if (event.target.closest('[data-rtsmto-view-rts]')) {
        this.lastRtsId = this.currentVariantId();
        this.activateTab('rts', true);
      }
    }

    // Tabs: roving tabindex; Left/Right/Home/End move AND activate.
    onKeyDown(event) {
      if (event.key === 'Escape') {
        if (this.zoomOpen) { var t = this.zoomOpen.querySelector('[data-rtsmto-zoom-trigger]'); this.closeZoom(); if (t && this.contains(document.activeElement)) t.focus(); }
        this.hidePop();
        return;
      }
      var choice = event.target.closest('[data-rtsmto-choice]');
      if (choice) { this.onChoiceKey(choice, event); return; }
      var tab = event.target.closest('[data-rtsmto-tab]');
      if (!tab) return;
      var index = this.tabs.indexOf(tab);
      var next = null;
      switch (event.key) {
        case 'ArrowLeft': next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length]; break;
        case 'ArrowRight': next = this.tabs[(index + 1) % this.tabs.length]; break;
        case 'Home': next = this.tabs[0]; break;
        case 'End': next = this.tabs[this.tabs.length - 1]; break;
        default: return;
      }
      event.preventDefault();
      this.activateTab(next.getAttribute('data-rtsmto-tab'), true);
    }

    onVariantChange(event) {
      var variant = event.detail && event.detail.variant;
      if (!variant || !variant.id) {
        // Unresolvable combination: nothing is selected, so no stale
        // "ready to ship" note. (A quick-view firing null only clears a note.)
        this.updateNote([]);
        return;
      }
      var id = String(variant.id);
      if (!this.map[id]) return; // another product (quick view etc.)
      var options = variant.options || this.map[id].o || [];
      this.syncSelects(options);
      this.markRow(id);
      this.updateNote(options);
    }

    onVariantsHydrated(event) {
      var detail = event.detail || {};
      if (!detail.map) return;
      if (detail.handle && this.handle && detail.handle !== this.handle) return;
      clearTimeout(this.hydrateTimer);
      this.mergeHydrated(detail.map, true);
    }

    // Idempotent: ids already in the map are skipped, so the theme event and
    // the fetchLatePages() fallback can both land.
    mergeHydrated(extra) {
      var added = [];
      Object.keys(extra).forEach(function (id) {
        if (this.map[id]) return;
        this.map[id] = extra[id];
        this.order.push(id);
        added.push(id);
      }, this);
      this.hydrated = true;

      added.forEach(function (id) {
        if (this.map[id].s === 'in_stock') this.appendRow(id, this.map[id]);
      }, this);
      if (this.rowsEl && this.rowsEl.querySelector('[data-rtsmto-row]') && this.toggle && this.toggle.hidden) {
        this.revealToggle();
      }

      // Late rows pushed a short list past the threshold: collapse it (first
      // 5 + "Show all") unless the selected row sits beyond the fifth.
      var rows = this.rows();
      if (!this.wasCollapsible && !this.userToggledMore && rows.length > COLLAPSE_OVER) {
        var selected = this.rowsEl.querySelector('[data-rtsmto-row].is-selected');
        this.expanded = !!selected && rows.indexOf(selected) >= VISIBLE_ROWS;
      }
      this.wasCollapsible = rows.length > COLLAPSE_OVER;
      this.updateMore();

      // A combination picked before the late pages arrived: theme.js appends
      // them to the product JSON before firing its event (and did so already
      // if it fired before we connected), so re-fire so it resolves. The
      // inputs already hold these values, so a repeat is harmless.
      var values = this.currentOptions();
      if (this.pendingUnresolved && this.findVariant(values)) {
        this.pendingUnresolved = false;
        this.writeOptions(values);
      } else {
        var current = this.findVariant(values);
        if (current) {
          this.markRow(current.id);
          this.updateNote(values);
        }
      }
    }

    // ── Tabs ───────────────────────────────────────────────────────────────

    // No stock at render time → the snippet leaves out the toggle and the
    // MTO panel's tabpanel semantics. Add them when late stock shows up.
    revealToggle() {
      this.toggle.hidden = false;
      // TICK-4268: the Made-to-Order-only header gives way to the real tabs.
      var head = this.querySelector('[data-rtsmto-mto-head]');
      if (head) head.hidden = true;
      var tab = this.querySelector('[data-rtsmto-tab="mto"]');
      var panel = this.querySelector('[data-rtsmto-panel="mto"]');
      if (panel && tab && !panel.hasAttribute('role')) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
      }
    }

    activateTab(name, focus) {
      var was = this.activeTab();
      if (was === 'mto' && name !== 'mto') this.mtoOptions = this.currentOptions();
      this.closeZoom();
      this.hidePop();
      this.tabs.forEach(function (tab) {
        var on = tab.getAttribute('data-rtsmto-tab') === name;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
        if (on && focus) tab.focus();
      });
      Array.prototype.forEach.call(this.querySelectorAll('[data-rtsmto-panel]'), function (panel) {
        panel.hidden = panel.getAttribute('data-rtsmto-panel') !== name;
      });

      if (name === 'rts') {
        var row = this.rowById(this.lastRtsId) || this.rowById(this.currentVariantId()) || this.firstRow();
        if (row) {
          var input = row.querySelector('.rtsmto-row__input');
          if (input) input.checked = true;
          this.lastRtsId = row.getAttribute('data-variant-id');
          if (this.indexOfRow(row) >= VISIBLE_ROWS && !this.expanded) {
            this.expanded = true;
            this.updateMore();
          }
          this.markRow(this.lastRtsId);
          if (this.lastRtsId !== this.currentVariantId()) this.writeOptions(this.rowOptions(row));
        }
      } else {
        // Switching back restores Made to Order's own selection. The first
        // visit moves off an in-stock combination to the nearest made-to-order
        // one, so the tab doesn't open on a piece that belongs on Ready to Ship.
        var target = (this.mtoOptions && this.findVariant(this.mtoOptions)) ? this.mtoOptions : null;
        if (!target) {
          var cur = this.findVariant(this.currentOptions());
          if (cur && cur.entry.s === 'in_stock') {
            var near = this.nearestMto(this.currentOptions());
            if (near) target = near.entry.o;
          }
        }
        if (target && !sameOptions(target, this.currentOptions())) {
          this.syncSelects(target);
          this.writeOptions(target);
        } else {
          this.syncSelects(this.currentOptions());
        }
      }
      this.setAttribute('data-active-tab', name);
      this.updateNote(this.currentOptions());
      if (was !== name) {
        document.dispatchEvent(new CustomEvent('rtsmto:tab', { detail: { tab: name, picker: this } }));
      }
    }

    activeTab() {
      var on = this.tabs.filter(function (tab) { return tab.getAttribute('aria-selected') === 'true'; })[0];
      if (this.toggle && this.toggle.hidden) return 'mto';
      return on ? on.getAttribute('data-rtsmto-tab') : 'mto';
    }

    // ── Rows ───────────────────────────────────────────────────────────────

    rows() {
      return this.rowsEl ? Array.prototype.slice.call(this.rowsEl.querySelectorAll('[data-rtsmto-row]')) : [];
    }

    firstRow() {
      return this.rows()[0] || null;
    }

    indexOfRow(row) {
      return this.rows().indexOf(row);
    }

    rowById(id) {
      if (!id || !this.rowsEl) return null;
      return this.rowsEl.querySelector('[data-rtsmto-row][data-variant-id="' + id + '"]');
    }

    rowOptions(row) {
      try { return JSON.parse(row.getAttribute('data-options')) || []; } catch (e) { return []; }
    }

    currentVariantId() {
      var found = this.findVariant(this.currentOptions());
      if (found) return found.id;
      var idInput = this.section.querySelector('[data-product-form] [name="id"]');
      return idInput ? String(idInput.value) : null;
    }

    markRow(id) {
      this.rows().forEach(function (row) {
        var on = row.getAttribute('data-variant-id') === String(id);
        row.classList.toggle('is-selected', on);
        var input = row.querySelector('.rtsmto-row__input');
        if (input && input.checked !== on) input.checked = on;
      });
    }

    updateMore() {
      var rows = this.rows();
      var collapsible = rows.length > COLLAPSE_OVER;
      rows.forEach(function (row, i) {
        row.hidden = collapsible && !this.expanded && i >= VISIBLE_ROWS;
      }, this);
      if (!this.moreBtn) return;
      this.moreBtn.hidden = !collapsible;
      this.moreBtn.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
      var label = this.moreBtn.querySelector('[data-rtsmto-more-label]');
      if (label) {
        label.textContent = this.expanded
          ? (this.strings.showFewer || '')
          : String(this.strings.showAll || '').replace('%n%', rows.length);
      }
    }

    badgeText(q) {
      if (q === 1) return this.strings.countOne || '';
      if (q > 1) return String(this.strings.countOther || '').replace('%n%', q);
      return this.strings.inStock || '';
    }

    // Same structure as the Liquid rows — keep the two in sync.
    appendRow(id, entry) {
      if (!this.rowsEl || this.rowById(id)) return;
      var values = entry.o || [];
      var roles = this.roles;
      var order = ['size', 'kind', 'swatch', 'top', 'upholstery', 'base', 'combo', 'orientation', 'layout', 'style', 'other'];
      var ordered = [];
      order.forEach(function (role) {
        roles.forEach(function (r, i) { if (r === role) ordered.push(i); });
      });
      var posOf = function (role) { return roles.indexOf(role); };
      var namePos = posOf('upholstery');
      if (namePos === -1) namePos = posOf('top');
      if (namePos === -1) {
        for (var k = 0; k < ordered.length; k++) {
          if (roles[ordered[k]] !== 'size' && roles[ordered[k]] !== 'orientation') { namePos = ordered[k]; break; }
        }
      }
      if (namePos === -1) namePos = posOf('size');
      if (namePos === -1) namePos = 0;
      var meta = ordered.filter(function (i) { return i !== namePos && values[i]; }).map(function (i) { return values[i]; }).join(' · ');

      var inputName = '';
      var sample = this.rowsEl.querySelector('.rtsmto-row__input');
      if (sample) inputName = sample.name;
      else inputName = 'rtsmto-rts-' + (this.inputFor(0) ? this.inputFor(0).getAttribute('form') : 'picker');

      function el(tag, cls, attrs) {
        var n = document.createElement(tag);
        if (cls) n.className = cls;
        Object.keys(attrs || {}).forEach(function (k) { n.setAttribute(k, attrs[k]); });
        return n;
      }
      var parts = this.chipParts;
      var textId = inputName + '-' + id;

      var label = el('label', 'rtsmto-row', { 'data-rtsmto-row': '', 'data-variant-id': id, 'data-options': JSON.stringify(values) });
      if (entry.q > 0) label.setAttribute('data-count', entry.q);
      var main = el('span', 'rtsmto-row__main');
      label.appendChild(main);

      var input = el('input', 'rtsmto-row__input', { type: 'radio', name: inputName, value: id, 'aria-labelledby': textId });
      main.appendChild(input);
      main.appendChild(el('span', 'rtsmto-row__radio', { 'aria-hidden': 'true' }));

      var zoom = el('span', 'rtsmto-zoom', { 'data-rtsmto-zoom': '' });
      var zoomLabel = this.strings.zoom || '';
      function half(part, cls) {
        var h = el('span', cls, { 'data-rtsmto-swatch': String(part).replace(/[ab]$/, '') });
        var which = String(part).slice(-1);
        if (which === 'a' || which === 'b') h.setAttribute('data-rtsmto-part', which);
        return h;
      }
      if (parts.length) {
        var chip = el('button', 'rtsmto-chip' + (parts.length === 1 ? ' rtsmto-chip--single' : ''), { type: 'button', 'data-rtsmto-zoom-trigger': '', 'aria-expanded': 'false', 'aria-label': zoomLabel });
        parts.forEach(function (part) { chip.appendChild(half(part, 'rtsmto-chip__half')); });
        zoom.appendChild(chip);
        var badgeBtn = el('button', 'rtsmto-zoom__badge', { type: 'button', 'data-rtsmto-zoom-trigger': '', 'aria-expanded': 'false', 'aria-label': zoomLabel, tabindex: '-1' });
        var dotWrap = el('span', 'rtsmto-zoom__badge-dot', { 'aria-hidden': 'true' });
        dotWrap.innerHTML = '<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.5 15.5 21 21"/></svg>';
        badgeBtn.appendChild(dotWrap);
        zoom.appendChild(badgeBtn);
      } else {
        var plain = el('span', 'rtsmto-chip rtsmto-chip--single', { 'aria-hidden': 'true' });
        plain.appendChild(el('span', 'rtsmto-chip__half'));
        zoom.appendChild(plain);
      }
      main.appendChild(zoom);

      var text = el('span', 'rtsmto-row__text', { id: textId });
      var name = el('span', 'rtsmto-row__name');
      name.textContent = values[namePos] || '';
      text.appendChild(name);
      if (meta) {
        var metaEl = el('span', 'rtsmto-row__meta');
        metaEl.textContent = meta;
        text.appendChild(metaEl);
      }
      var badge = el('span', 'rtsmto-badge');
      badge.appendChild(el('span', 'rtsmto-badge__dot', { 'aria-hidden': 'true' }));
      badge.appendChild(document.createTextNode(this.badgeText(entry.q)));
      text.appendChild(badge);
      main.appendChild(text);

      // Close-up tiles — same as the Liquid: name per material (TICK-4268:
      // no label line; --n<count> sizes three tiles to the pane).
      if (parts.length) {
        var closeup = el('span', 'rtsmto-closeup rtsmto-closeup--n' + parts.length + (parts.length === 1 ? ' rtsmto-closeup--single' : ''), { 'data-rtsmto-closeup': '' });
        parts.forEach(function (part) {
          var index = parseInt(String(part), 10);
          var which = String(part).slice(-1);
          var nameText = values[index] || '';
          if (which === 'a' || which === 'b') {
            var halves = splitCombo(values[index] || '');
            nameText = which === 'a' ? halves[0] : halves[1];
          }
          var item = el('span', 'rtsmto-closeup__item');
          item.appendChild(half(part, 'rtsmto-closeup__img'));
          var nm = el('span', 'rtsmto-closeup__name'); nm.textContent = nameText; item.appendChild(nm);
          closeup.appendChild(item);
        });
        label.appendChild(closeup);
      }

      this.rowsEl.appendChild(label);
      this.paintRow(label);
    }

    // ── MTO selects + note ─────────────────────────────────────────────────

    syncSelects(values) {
      this.selects.forEach(function (select) {
        var index = parseInt(select.getAttribute('data-index'), 10) || 0;
        var value = values[index];
        if (value == null) return;
        if (select.value !== String(value)) select.value = String(value);
        this.syncGroup(select);
      }, this);
    }

    // Tiles/buttons mirror their select: aria-checked + roving tabindex, and
    // the label row names the chosen value.
    syncGroup(select) {
      var group = select.closest('[data-rtsmto-group]');
      if (!group) return;
      var value = select.value;
      Array.prototype.forEach.call(group.querySelectorAll('[data-rtsmto-choice]'), function (btn) {
        var on = btn.getAttribute('data-value') === value;
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
        btn.setAttribute('tabindex', on ? '0' : '-1');
      });
      var shown = group.querySelector('[data-rtsmto-group-value]');
      if (shown && !group.hasAttribute('data-previewing')) shown.textContent = value;
      this.syncQuickRow(group, value);
      this.filterChoices();
      var drawer = this.drawerFor(parseInt(group.getAttribute('data-index'), 10));
      if (drawer) {
        Array.prototype.forEach.call(drawer.querySelectorAll('[data-rtsmto-drawer-choice]'), function (t) {
          t.setAttribute('aria-checked', t.getAttribute('data-value') === value ? 'true' : 'false');
        });
      }
    }

    // A value that isn't in the quick row (picked in the full picker, or set
    // by a Ready to Ship row) rebuilds the row around it, so the choice stays
    // visible (handoff, "Recommended"). aria-checked was set by syncGroup.
    syncQuickRow(group, value) {
      var quick = Array.prototype.slice.call(group.querySelectorAll('[data-rtsmto-quick]'));
      if (!quick.length) return;
      var hit = quick.some(function (b) { return b.getAttribute('data-value') === value; });
      if (hit) return;
      this.rebuildQuickRow(group, value);
    }

    // David 11-02 (Material Swatches product): a 'swatch' group shows only the
    // colours whose variants carry the chosen 'kind' (Fabric / Leather / …).
    // Picking a kind already lands on its closest variant (closestVariant),
    // so this only hides and shows tiles.
    filterChoices() {
      this.groups.forEach(function (group) {
        var by = group.getAttribute('data-filter-index');
        if (by == null) return;
        var select = this.selectFor(parseInt(by, 10));
        if (!select) return;
        var key = '§' + select.value + '§';
        Array.prototype.forEach.call(group.querySelectorAll('[data-rtsmto-choice][data-parents]'), function (btn) {
          btn.hidden = btn.getAttribute('data-parents').indexOf(key) === -1;
        });
      }, this);
    }

    groupFor(index) {
      for (var i = 0; i < this.groups.length; i++) {
        if (parseInt(this.groups[i].getAttribute('data-index'), 10) === index) return this.groups[i];
      }
      return null;
    }

    // TICK-4268 (Jody 10-28): the full picker's choice carries over to the
    // main quick row — the picked value FIRST, then other values from its
    // collection (option order), topped up with the row's original values
    // (kept on the group at the first rebuild). Same number of buttons, so
    // "+N" stays total − shown. Roving tabindex follows the select's value.
    rebuildQuickRow(group, value) {
      if (!group || !value) return;
      var quick = Array.prototype.slice.call(group.querySelectorAll('[data-rtsmto-quick]'));
      if (!quick.length) return;
      if (!group.rtsmtoQuickOrig) {
        group.rtsmtoQuickOrig = quick.map(function (b) { return b.getAttribute('data-value'); });
      }
      var self = this;
      var index = parseInt(group.getAttribute('data-index'), 10) || 0;
      var select = group.querySelector('[data-rtsmto-select]');
      var all = select ? Array.prototype.map.call(select.options, function (o) { return o.value; }) : [];
      var key = this.collectionOf(value).key;
      var list = [value];
      function add(v) {
        if (list.length < quick.length && v && list.indexOf(v) === -1) list.push(v);
      }
      all.forEach(function (v) { if (self.collectionOf(v).key === key) add(v); });
      group.rtsmtoQuickOrig.forEach(add);
      all.forEach(add);
      var current = select ? select.value : value;
      var anyOn = list.indexOf(current) !== -1;
      quick.forEach(function (btn, i) {
        var v = list[i] || btn.getAttribute('data-value');
        var on = anyOn ? v === current : i === 0;
        btn.setAttribute('data-value', v);
        btn.setAttribute('aria-label', v);
        btn.setAttribute('aria-checked', anyOn && on ? 'true' : 'false');
        btn.setAttribute('tabindex', on ? '0' : '-1');
        var img = btn.querySelector('[data-rtsmto-tile]');
        if (img && img.getAttribute('data-value') !== v) self.paintValue(img, index, v);
      });
      var more = group.querySelector('.rtsmto-choice--more');
      if (more) more.textContent = '+' + Math.max(0, all.length - quick.length);
    }

    updateNote(values) {
      if (!this.note) return;
      var found = this.findVariant(values);
      var show = this.activeTab() === 'mto' && found && found.entry.s === 'in_stock' && !!this.rowById(found.id);
      // TICK-4268 (David 10-28): [data-rts-match] on the root puts a check on
      // the selected tiles (CSS) while the callout shows.
      this.toggleAttribute('data-rts-match', !!show);
      if (!show) {
        if (this.note.childNodes.length) this.note.textContent = '';
        this.note.classList.remove('is-active');
        return;
      }
      // Iteration 4 / Laleh: the Made to Order pane never says "in stock" —
      // it points at the faster route instead (no count). TICK-4268: a
      // highlighted callout, sentence + bold "View In-Stock Options →".
      this.note.textContent = '';
      var text = document.createElement('span');
      text.className = 'rtsmto-mto-note__text';
      text.textContent = this.strings.sooner || '';
      this.note.appendChild(text);
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'rtsmto-link';
      button.setAttribute('data-rtsmto-view-rts', '');
      button.textContent = this.strings.viewRts || '';
      var arrow = document.createElement('span');
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = ' →';
      button.appendChild(arrow);
      this.note.appendChild(button);
      this.note.classList.add('is-active');
    }

    // ── Swatches ───────────────────────────────────────────────────────────

    // Sync sources: server map (native / Files), then the exact overrides.
    lookup(optionName, value) {
      var key = normalizeKey(optionName || '') + '||' + normalizeKey(value);
      var hit = this.swatches[key];
      if (hit && (hit.img || hit.color)) return hit;
      var override = this.overrides[key];
      if (typeof override === 'string' && override) return { img: override };
      return null;
    }

    // cb({img}|{color}|null) — sync when possible, async for the CDN probe.
    // part: '' (whole value), 'a' or 'b' (the two halves of a combo value).
    resolve(index, value, cb, part) {
      if (value == null || value === '') { cb(null); return; }
      var hit = this.lookup(this.optionNames[index], value);
      if (hit) { cb(hit); return; }
      var role = this.roles[index];
      if (role === 'combo') {
        var halves = splitCombo(value);
        if (part === 'a') { this.resolveIn('Upholstery', halves[0], cb); return; }
        if (part === 'b') { this.resolveIn('Finish', halves[1], cb); return; }
        cb(null);
        return;
      }
      var folder = MATERIAL[role];
      if (!folder) { cb(null); return; }
      this.resolveIn(folder, value, cb);
    }

    // A bare value against one Swatch King folder (overrides first, matched
    // on value alone since a combo half has no option name of its own).
    resolveIn(folder, value, cb) {
      if (!value) { cb(null); return; }
      var wanted = '||' + normalizeKey(value);
      // Files first (the server map's "combo||<half>" entries, or the same
      // material under any option name), then overrides, then Swatch King.
      var own = Object.keys(this.swatches);
      for (var j = 0; j < own.length; j++) {
        if (own[j].slice(-wanted.length) === wanted && this.swatches[own[j]].img) { cb(this.swatches[own[j]]); return; }
      }
      var keys = Object.keys(this.overrides);
      for (var i = 0; i < keys.length; i++) {
        if (keys[i].slice(-wanted.length) === wanted) { cb({ img: this.overrides[keys[i]] }); return; }
      }
      var url = SK_BASE + folder + '/100/' + skEncode(value) + '.webp';
      probe(url, function (ok) { cb(ok ? { img: url } : null); });
    }

    // Combo values paint as a vertical split (two backgrounds) on one element.
    paintValue(el, index, value) {
      var self = this;
      el.setAttribute('data-value', value);
      this.applySwatch(el, null);
      if (this.roles[index] !== 'combo' || this.lookup(this.optionNames[index], value)) {
        this.resolve(index, value, function (swatch) {
          if (el.getAttribute('data-value') === value) self.applySwatch(el, swatch);
        });
        return;
      }
      var got = { a: undefined, b: undefined };
      ['a', 'b'].forEach(function (part) {
        self.resolve(index, value, function (swatch) {
          got[part] = swatch || null;
          if (got.a === undefined || got.b === undefined) return;
          if (el.getAttribute('data-value') !== value) return;
          var a = swatchCss(got.a);
          var b = swatchCss(got.b);
          if (!a && !b) return;
          el.style.backgroundColor = '';
          el.style.backgroundImage = [a || 'linear-gradient(#ececec, #ececec)', b || 'linear-gradient(#ececec, #ececec)'].join(', ');
          el.style.backgroundSize = '50% 100%, 50% 100%';
          el.style.backgroundPosition = 'left center, right center';
          el.style.backgroundRepeat = 'no-repeat';
        }, part);
      });
    }

    applySwatch(el, swatch) {
      if (!el) return;
      el.style.backgroundSize = '';
      el.style.backgroundPosition = '';
      el.style.backgroundRepeat = '';
      if (swatch && swatch.img) {
        el.style.backgroundImage = 'url("' + String(swatch.img).replace(/"/g, '%22') + '")';
        el.style.backgroundColor = '';
      } else if (swatch && swatch.color) {
        el.style.backgroundImage = '';
        el.style.backgroundColor = swatch.color;
      } else {
        el.style.backgroundImage = '';
        el.style.backgroundColor = '';
      }
    }

    paintRow(row) {
      var values = this.rowOptions(row);
      Array.prototype.forEach.call(row.querySelectorAll('[data-rtsmto-swatch]'), function (half) {
        var index = parseInt(half.getAttribute('data-rtsmto-swatch'), 10);
        var part = half.getAttribute('data-rtsmto-part') || '';
        var self = this;
        this.resolve(index, values[index], function (swatch) { self.applySwatch(half, swatch); }, part);
      }, this);
    }

    paintGroup(group) {
      var index = parseInt(group.getAttribute('data-index'), 10) || 0;
      Array.prototype.forEach.call(group.querySelectorAll('[data-rtsmto-choice]'), function (btn) {
        var img = btn.querySelector('[data-rtsmto-tile]');
        if (img) this.paintValue(img, index, btn.getAttribute('data-value'));
      }, this);
    }

    // ── Made to Order keyboard: radiogroup arrows ─────────────────────────

    onChoiceKey(btn, event) {
      var group = btn.closest('[data-rtsmto-group]');
      var all = Array.prototype.slice.call(group.querySelectorAll('[data-rtsmto-choice]'));
      var i = all.indexOf(btn);
      var next = null;
      switch (event.key) {
        case 'ArrowRight': case 'ArrowDown': next = all[(i + 1) % all.length]; break;
        case 'ArrowLeft': case 'ArrowUp': next = all[(i - 1 + all.length) % all.length]; break;
        case 'Home': next = all[0]; break;
        case 'End': next = all[all.length - 1]; break;
        case ' ': case 'Enter': event.preventDefault(); this.chooseValue(btn); return;
        default: return;
      }
      event.preventDefault();
      next.focus();
      this.chooseValue(next);
    }

    // ── Hover: RTS close-up (100 ms) and MTO tile card (250 ms) ───────────

    onOver(event) {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      if (!canHover()) return;
      var zone = event.target.closest('[data-rtsmto-zoom]');
      if (zone && this.contains(zone)) {
        var row = zone.closest('[data-rtsmto-row]');
        if (this.zoomPinned || this.zoomOpen === row) return;
        var self = this;
        clearTimeout(this.zoomTimer);
        this.zoomTimer = setTimeout(function () { self.openZoom(row, false); }, this.zoomDelay);
        return;
      }
      var choice = event.target.closest('[data-rtsmto-choice]');
      if (choice && this.contains(choice)) this.previewChoice(choice, false);
    }

    onOut(event) {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      var zone = event.target.closest('[data-rtsmto-zoom]');
      if (zone && !(event.relatedTarget && zone.contains(event.relatedTarget))) {
        clearTimeout(this.zoomTimer);
        if (!this.zoomPinned) this.closeZoom();
      }
      var choice = event.target.closest('[data-rtsmto-choice]');
      if (choice && !(event.relatedTarget && choice.contains(event.relatedTarget))) this.hidePop();
    }

    // Keyboard: focusing a tile opens its card at once (no delay); leaving
    // the picker closes it.
    onFocusIn(event) {
      var choice = event.target.closest('[data-rtsmto-choice]');
      if (choice && canHover() && choice.matches(':focus-visible')) this.previewChoice(choice, true);
    }

    onFocusOut(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.hidePop();
      if (!this.zoomPinned) this.closeZoom();
    }

    openZoom(row, pin) {
      if (!row) return;
      if (this.zoomOpen && this.zoomOpen !== row) this.closeZoom();
      this.zoomOpen = row;
      this.zoomPinned = !!pin;
      row.classList.add('is-zoomed');
      Array.prototype.forEach.call(row.querySelectorAll('[data-rtsmto-zoom-trigger]'), function (b) { b.setAttribute('aria-expanded', 'true'); });
    }

    closeZoom() {
      clearTimeout(this.zoomTimer);
      var row = this.zoomOpen;
      this.zoomOpen = null;
      this.zoomPinned = false;
      if (!row) return;
      row.classList.remove('is-zoomed');
      Array.prototype.forEach.call(row.querySelectorAll('[data-rtsmto-zoom-trigger]'), function (b) { b.setAttribute('aria-expanded', 'false'); });
    }

    // Hovering a tile names it in the group's label row at once; the 150 px
    // card follows after the delay (tiles only — text buttons just rename).
    previewChoice(btn, now) {
      var group = btn.closest('[data-rtsmto-group]');
      if (!group) return;
      this.hidePop();
      var shown = group.querySelector('[data-rtsmto-group-value]');
      if (shown) shown.textContent = btn.getAttribute('data-value');
      group.setAttribute('data-previewing', '');
      this.popGroup = group;
      var img = btn.querySelector('[data-rtsmto-tile]');
      if (!img || !this.pop) return;
      var self = this;
      clearTimeout(this.tileTimer);
      this.tileTimer = setTimeout(function () { self.showPop(btn, group, img); }, now ? 0 : this.tileDelay);
    }

    showPop(btn, group, img) {
      var pop = this.pop;
      var name = pop.querySelector('[data-rtsmto-pop-name]');
      if (name) name.textContent = btn.getAttribute('data-value');
      var big = pop.querySelector('[data-rtsmto-pop-img]');
      if (big) {
        big.style.backgroundImage = img.style.backgroundImage;
        big.style.backgroundColor = img.style.backgroundColor;
        big.style.backgroundSize = img.style.backgroundSize;
        big.style.backgroundPosition = img.style.backgroundPosition;
        big.style.backgroundRepeat = img.style.backgroundRepeat;
      }
      pop.hidden = false;
      // Above the tile, clamped to the picker's edges so it never overflows
      // the panel; the arrow keeps pointing at the tile.
      var host = this.getBoundingClientRect();
      var r = btn.getBoundingClientRect();
      var w = pop.offsetWidth;
      var h = pop.offsetHeight;
      var center = r.left + r.width / 2 - host.left;
      var left = Math.max(0, Math.min(host.width - w, center - w / 2));
      pop.style.left = left + 'px';
      pop.style.top = (r.top - host.top - h - 12) + 'px';
      pop.style.setProperty('--rtsmto-pop-arrow', (center - left) + 'px');
      pop.classList.remove('is-in');
      void pop.offsetWidth; // restart the entrance animation
      pop.classList.add('is-in');
    }

    hidePop() {
      clearTimeout(this.tileTimer);
      if (this.pop) { this.pop.hidden = true; this.pop.classList.remove('is-in'); }
      var group = this.popGroup;
      this.popGroup = null;
      if (!group) return;
      group.removeAttribute('data-previewing');
      var select = group.querySelector('[data-rtsmto-select]');
      var shown = group.querySelector('[data-rtsmto-group-value]');
      if (select && shown) shown.textContent = select.value;
    }

    // ── Full material picker (large ranges, Iteration 4 task 7) ───────────
    // Desktop: 460 px drawer from the right; phone: 88% bottom sheet. Hover
    // previews a material, click/tap selects it. Esc, the close button or
    // the scrim close it; focus is trapped inside and returns to the opener.

    drawerFor(index) {
      if (!this.drawers) return null;
      for (var i = 0; i < this.drawers.length; i++) {
        if (parseInt(this.drawers[i].getAttribute('data-index'), 10) === index) return this.drawers[i];
      }
      return null;
    }

    // TICK-4268 (Jody 10-28): every Linen collection ("Linen 651", "L653" …)
    // is one "linen" category — same rule as the drawer Liquid, keep in sync.
    collectionOf(value) {
      var key = String(value).split('|')[0].trim().toLowerCase().replace(/ leather$/, '');
      if (key.indexOf('linen') !== -1 || /^l ?65\d/.test(key)) key = 'linen';
      var data = this.collections[key];
      var name = data && data.name ? data.name : key.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
      return { key: key, name: name, comp: (data && data.comp) || '' };
    }

    openDrawer(id, opener) {
      var drawer = this.drawers.filter(function (d) { return d.id === id; })[0];
      if (!drawer) return;
      var index = parseInt(drawer.getAttribute('data-index'), 10) || 0;
      this.hidePop();
      if (!drawer.rtsmtoReady) this.setupDrawer(drawer, index);
      this.drawerOpen = drawer;
      this.drawerOpener = opener || null;
      var select = this.selectFor(index);
      this.previewDrawer(drawer, select ? select.value : '', true);
      drawer.hidden = false;
      document.documentElement.classList.add('rtsmto-locked');
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      var current = drawer.querySelector('[data-rtsmto-drawer-choice][aria-checked="true"]');
      if (current && current.scrollIntoView) current.scrollIntoView({ block: 'nearest' });
      var close = drawer.querySelector('.rtsmto-drawer__close');
      if (close) close.focus();
    }

    closeDrawer() {
      var drawer = this.drawerOpen;
      if (!drawer) return;
      this.drawerOpen = null;
      // TICK-4268: after a pick, the quick row shows the drawer's selection on
      // the way out (an open-and-close without a pick leaves the row alone).
      var index = parseInt(drawer.getAttribute('data-index'), 10) || 0;
      var select = this.selectFor(index);
      if (select && this.drawerPicked) this.rebuildQuickRow(this.groupFor(index), select.value);
      this.drawerPicked = false;
      drawer.classList.remove('is-open');
      drawer.hidden = true;
      document.documentElement.classList.remove('rtsmto-locked');
      if (this.drawerOpener && this.drawerOpener.isConnected) this.drawerOpener.focus();
    }

    selectFor(index) {
      for (var i = 0; i < this.selects.length; i++) {
        if (parseInt(this.selects[i].getAttribute('data-index'), 10) === index) return this.selects[i];
      }
      return null;
    }

    setupDrawer(drawer, index) {
      var self = this;
      drawer.rtsmtoReady = true;
      // Out of the product column: nothing in the page can sit above it.
      document.body.appendChild(drawer);
      Array.prototype.forEach.call(drawer.querySelectorAll('[data-rtsmto-coll-name]'), function (el) {
        el.textContent = self.collectionOf(el.getAttribute('data-rtsmto-coll-name') + ' |').name;
      });
      Array.prototype.forEach.call(drawer.querySelectorAll('[data-rtsmto-drawer-choice]'), function (t) {
        var img = t.querySelector('[data-rtsmto-drawer-img]');
        if (img) self.paintValue(img, index, t.getAttribute('data-value'));
      });
      var body = drawer.querySelector('.rtsmto-drawer__body');

      drawer.addEventListener('click', function (event) {
        if (event.target.closest('[data-rtsmto-drawer-close]')) { self.closeDrawer(); return; }
        var tile = event.target.closest('[data-rtsmto-drawer-choice]');
        if (tile) { self.pickFromDrawer(drawer, index, tile); return; }
        var chip = event.target.closest('[data-rtsmto-filter]');
        if (chip) self.filterDrawer(drawer, chip.getAttribute('data-rtsmto-filter'));
      });
      drawer.addEventListener('pointerover', function (event) {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        var tile = event.target.closest('[data-rtsmto-drawer-choice]');
        if (tile) self.previewDrawer(drawer, tile.getAttribute('data-value'), tile.getAttribute('aria-checked') === 'true');
      });
      if (body) {
        body.addEventListener('pointerleave', function () {
          var select = self.selectFor(index);
          if (select) self.previewDrawer(drawer, select.value, true);
        });
      }
      drawer.addEventListener('focusin', function (event) {
        var tile = event.target.closest('[data-rtsmto-drawer-choice]');
        if (tile) self.previewDrawer(drawer, tile.getAttribute('data-value'), tile.getAttribute('aria-checked') === 'true');
      });
      drawer.addEventListener('keydown', function (event) { self.onDrawerKey(drawer, index, event); });
    }

    pickFromDrawer(drawer, index, tile) {
      var value = tile.getAttribute('data-value');
      var select = this.selectFor(index);
      if (select && select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.drawerPicked = true;
      this.rebuildQuickRow(this.groupFor(index), value);
      this.previewDrawer(drawer, value, true);
    }

    previewDrawer(drawer, value, isSelected) {
      if (!value) return;
      var coll = this.collectionOf(value);
      var name = String(value).indexOf('|') === -1 ? value : String(value).split('|').slice(1).join('|').trim();
      var set = function (sel, text) { var el = drawer.querySelector(sel); if (el) el.textContent = text; };
      set('[data-rtsmto-preview-coll]', coll.name);
      set('[data-rtsmto-preview-name]', name);
      set('[data-rtsmto-preview-comp]', coll.comp);
      set('[data-rtsmto-preview-state]', isSelected ? (this.strings.selected || '') : (this.strings.previewing || ''));
      var img = drawer.querySelector('[data-rtsmto-preview-img]');
      var src = null;
      Array.prototype.forEach.call(drawer.querySelectorAll('[data-rtsmto-drawer-choice]'), function (t) {
        if (t.getAttribute('data-value') === value) src = t.querySelector('[data-rtsmto-drawer-img]');
      });
      if (img && src) {
        img.style.backgroundImage = src.style.backgroundImage;
        img.style.backgroundColor = src.style.backgroundColor;
        img.style.backgroundSize = src.style.backgroundSize;
        img.style.backgroundPosition = src.style.backgroundPosition;
        img.style.backgroundRepeat = src.style.backgroundRepeat;
      }
    }

    filterDrawer(drawer, key) {
      Array.prototype.forEach.call(drawer.querySelectorAll('[data-rtsmto-filter]'), function (chip) {
        chip.setAttribute('aria-pressed', chip.getAttribute('data-rtsmto-filter') === key ? 'true' : 'false');
      });
      Array.prototype.forEach.call(drawer.querySelectorAll('[data-rtsmto-coll]'), function (group) {
        group.hidden = !!key && group.getAttribute('data-rtsmto-coll') !== key;
      });
      var body = drawer.querySelector('.rtsmto-drawer__body');
      if (body) body.scrollTop = 0;
    }

    onDrawerKey(drawer, index, event) {
      if (event.key === 'Escape') { event.preventDefault(); this.closeDrawer(); return; }
      if (event.key === 'Tab') {
        // Focus trap: wrap between the first and last focusable controls.
        var focusables = Array.prototype.filter.call(
          drawer.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'),
          function (el) { return el.offsetParent !== null; }
        );
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        return;
      }
      var tile = event.target.closest('[data-rtsmto-drawer-choice]');
      if (!tile) return;
      var tiles = Array.prototype.filter.call(drawer.querySelectorAll('[data-rtsmto-drawer-choice]'), function (t) { return t.offsetParent !== null; });
      var i = tiles.indexOf(tile);
      var next = null;
      switch (event.key) {
        case 'ArrowRight': case 'ArrowDown': next = tiles[Math.min(tiles.length - 1, i + 1)]; break;
        case 'ArrowLeft': case 'ArrowUp': next = tiles[Math.max(0, i - 1)]; break;
        case 'Home': next = tiles[0]; break;
        case 'End': next = tiles[tiles.length - 1]; break;
        case 'Enter': case ' ': event.preventDefault(); this.pickFromDrawer(drawer, index, tile); return;
        default: return;
      }
      event.preventDefault();
      if (next) next.focus();
    }

    // A pinned close-up closes on any click outside its swatch.
    onDocPointer_(event) {
      if (!this.zoomOpen) return;
      var zone = this.zoomOpen.querySelector('[data-rtsmto-zoom]');
      if (!zone || !zone.contains(event.target)) this.closeZoom();
    }

    // Fabric Swatches fallback: fan up to 3 of this product's resolved
    // swatches; none → text only (no image column).
    // David 10-30 (wood products): always three — topped up from
    // data-defaults (Light / Latte / Espresso Oak) — fanned dark at the back
    // left to light at the front right.
    paintStack() {
      // The Fabric swatches block sits outside the picker (round 1), so look
      // in the product section.
      var stack = this.section.querySelector('[data-rtsmto-stack]');
      if (!stack) return;
      var block = stack.closest('[data-rtsmto-swatch-block]');
      var index = parseInt(stack.getAttribute('data-option-index'), 10) || 0;
      var values = [];
      var defaults = [];
      try { values = JSON.parse(stack.getAttribute('data-values')) || []; } catch (e) { values = []; }
      try { defaults = JSON.parse(stack.getAttribute('data-defaults') || '[]') || []; } catch (e) { defaults = []; }
      var self = this;
      var results = new Array(values.length);
      var pending = values.length;
      if (!pending) { build(); return; }
      values.forEach(function (value, i) {
        self.resolve(index, value, function (swatch) {
          results[i] = swatch && (swatch.img || swatch.color) ? { name: value, img: swatch.img, color: swatch.color } : null;
          pending--;
          if (pending === 0) build();
        });
      });
      // 0 = lightest … 3 = darkest, from the finish name.
      function shade(name) {
        var n = String(name || '').toLowerCase();
        if (/light|natural|white|bleach|blond|ash|birch|maple/.test(n)) return 0;
        if (/espresso|black|ebony|dark|charcoal|smoked/.test(n)) return 3;
        if (/walnut|teak|cherry|brown|tobacco|cognac/.test(n)) return 2;
        return 1;
      }
      function build() {
        var found = results.filter(Boolean);
        if (defaults.length) {
          defaults.forEach(function (d) {
            if (found.length >= 3) return;
            var dup = found.some(function (f) { return String(f.name).toLowerCase() === String(d.name).toLowerCase(); });
            if (!dup && d.img) found.push(d);
          });
          found.sort(function (a, b) { return shade(b.name) - shade(a.name); });
          // More than three: darkest, a middle one, lightest.
          if (found.length > 3) found = [found[0], found[Math.floor(found.length / 2)], found[found.length - 1]];
        } else {
          found = found.slice(0, 3);
        }
        stack.textContent = '';
        if (!found.length) {
          stack.hidden = true;
          if (block) block.classList.add('rtsmto-swatches--no-media');
          return;
        }
        var slots = found.length === 3 ? ['a', 'b', 'c'] : found.length === 2 ? ['a', 'c'] : ['solo'];
        found.forEach(function (swatch, i) {
          var chip = document.createElement('span');
          chip.className = 'rtsmto-swatches__chip rtsmto-swatches__chip--' + slots[i];
          self.applySwatch(chip, swatch);
          stack.appendChild(chip);
        });
        stack.hidden = false;
        if (block) block.classList.remove('rtsmto-swatches--no-media');
      }
    }

    paintAll() {
      this.rows().forEach(this.paintRow, this);
      this.groups.forEach(this.paintGroup, this);
      this.paintStack();
    }
  }

  customElements.define('rtsmto-picker', RtsmtoPicker);
})();
